import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import RequestLogCards from "./RequestLogCards";
import RequestLogsLoadingShimmer from "./RequestLogsLoadingShimmer";
import { useInfiniteScroll, useKeyboardNavigation } from "./hooks";

import { NoResultsFooter } from ".";

import LoadingSpinner from "@/components/LoadingSpinner";
import { SCROLL_REVIEW_LOGS_DRAWER_TO_TOP_EVENT } from "@/components/SidebarSearchLayout/Sidebar/SecondarySidebar/SearchBar/ReviewLogsDrawerControls/constants";
import { useRecentlyViewedRequestLogSession } from "@/components/RequestLogsTable/useRecentlyViewedRequestLogSession";
import { cn } from "@/lib/utils";

const getCanLoadAdjacentPages = ({
  currentId,
  currentType,
  positionedRequestId,
  requests,
}: {
  currentId: string;
  currentType: string;
  positionedRequestId: string | null;
  requests: { id: string | number; type?: string }[];
}): boolean => {
  if (!currentId) {
    return true;
  }
  if (positionedRequestId === currentId) {
    return true;
  }
  const currentIdInRequests = requests.some(
    item => String(item.id) === currentId && item.type === currentType,
  );
  return !currentIdInRequests;
};

// TODO (naveed) LOW: At ~50–100 items, even trimmed cards can overwhelm the browser.
// As I mentioned earlier, consider react-window or react-virtual on this <nav> so we only ever paint the ~10 items actually in view.
const RequestLogs = ({
  inputRef,
  requestsOn,
  requests,
  requestLogs,
  secondarySidebarOpen = true,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  requestsOn: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requests: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestLogs: UseInfiniteQueryResult<any, unknown>;
  secondarySidebarOpen?: boolean;
}) => {
  const { requestId, groupId } = useParams();
  const location = useLocation();
  const onGroupPage = location.pathname.includes("/group");
  const currentType = onGroupPage ? "group" : "request";
  const currentId = groupId || requestId || "";

  const sidebarLogContainerRef = useRef<HTMLDivElement | null>(null);
  const previousScrollPositionRef = useRef<{
    anchorOffsetTop: number | null;
    anchorRequestId: string | null;
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);
  const lastScrolledRequestIdRef = useRef<string | null>(null);
  const hasPositionedInitialRequestRef = useRef(false);
  const [positionedRequestId, setPositionedRequestId] = useState<string | null>(null);
  const { recentlyViewedRequestId, markRecentlyViewedRequest } =
    useRecentlyViewedRequestLogSession();

  useEffect(() => {
    const handleScrollToTop = () => {
      sidebarLogContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener(SCROLL_REVIEW_LOGS_DRAWER_TO_TOP_EVENT, handleScrollToTop);
    return () =>
      window.removeEventListener(SCROLL_REVIEW_LOGS_DRAWER_TO_TOP_EVENT, handleScrollToTop);
  }, []);

  useEffect(() => {
    lastScrolledRequestIdRef.current = null;
    hasPositionedInitialRequestRef.current = false;
    setPositionedRequestId(null);
  }, [currentId]);
  const {
    fetchNextPage: loadNextPage,
    fetchPreviousPage: loadPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = requestLogs;

  const fetchPreviousPage = useCallback(() => {
    if (!hasPreviousPage || isFetchingPreviousPage || !sidebarLogContainerRef.current) {
      return;
    }

    const container = sidebarLogContainerRef.current;
    const anchorElement = currentId
      ? (container.querySelector(`[data-request-id="${currentId}"]`) as HTMLElement | null)
      : null;
    const anchorOffsetTop = anchorElement
      ? anchorElement.getBoundingClientRect().top - container.getBoundingClientRect().top
      : null;
    previousScrollPositionRef.current = {
      anchorOffsetTop,
      anchorRequestId: currentId || null,
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    };

    loadPreviousPage();
  }, [currentId, hasPreviousPage, isFetchingPreviousPage, loadPreviousPage]);

  const fetchNextPage = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    loadNextPage();
  }, [hasNextPage, isFetchingNextPage, loadNextPage]);

  const canLoadAdjacentPages = getCanLoadAdjacentPages({
    currentId,
    currentType,
    positionedRequestId,
    requests,
  });
  const previousInfiniteScrollRef = useInfiniteScroll(
    fetchPreviousPage,
    canLoadAdjacentPages && !!hasPreviousPage && !isFetchingPreviousPage,
    sidebarLogContainerRef,
  );
  const nextInfiniteScrollRef = useInfiniteScroll(
    fetchNextPage,
    canLoadAdjacentPages && !!hasNextPage && !isFetchingNextPage,
    sidebarLogContainerRef,
  );

  // TODO (naveed) LOW: Each arrow‑key press triggers findIndex on the entire requests array, and we do it twice (getNextId + getPreviousId).
  // On a 100‑item list, that’s 200 array scans per key‑tap. (O(N) lookups). We should precompute a map and use that for O(1) lookups.
  const getNextId = useCallback(
    (id?: string, type?: string) => {
      const index = requests.findIndex(
        (item: { id: string; type?: string; trace_id?: string }) =>
          String(item.id) === id && item.type === type,
      );
      if (index < requests.length - 1) {
        const nextItem = requests[index + 1];
        return {
          id: nextItem.id,
          type: nextItem.type,
        };
      }
      return null;
    },
    [requests],
  );

  const scrollToLogBox = useCallback(
    (id: string, behavior: ScrollBehavior = "smooth") => {
      if (sidebarLogContainerRef.current) {
        const container = sidebarLogContainerRef.current;
        const element = container.querySelector(`[data-request-id="${id}"]`) as HTMLElement;

        if (element) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const offsetTop = elementRect.top - containerRect.top;
          const containerHeight = container.clientHeight;
          const elementHeight = element.clientHeight;
          const padding = 16;
          let scrollTo: number | null = null;

          if (offsetTop < padding) {
            scrollTo = container.scrollTop + offsetTop - padding;
          }
          if (offsetTop >= padding && offsetTop + elementHeight > containerHeight - padding) {
            scrollTo = container.scrollTop + offsetTop - containerHeight + elementHeight + padding;
          }

          if (scrollTo === null) {
            return true;
          }

          container.scrollTo({
            top: scrollTo,
            behavior,
          });
          return true;
        }
      }
      return false;
    },
    [sidebarLogContainerRef],
  );

  const getPreviousId = useCallback(
    (id?: string, type?: string) => {
      const index = requests.findIndex(
        (item: { id: string; type?: string; trace_id?: string }) =>
          String(item.id) === id && item.type === type,
      );
      if (index > 0) {
        const prevItem = requests[index - 1];
        return {
          id: prevItem.id,
          type: prevItem.type,
        };
      }
      return null;
    },
    [requests],
  );

  useKeyboardNavigation({
    inputRef,
    items: requests,
    getNextId,
    getPreviousId,
    scrollToLogBox,
    currentId,
    currentType,
    onGroupPage,
    secondarySidebarOpen,
  });

  useEffect(() => {
    if (!currentId || requests.length === 0) {
      return;
    }

    if (lastScrolledRequestIdRef.current === currentId) {
      return;
    }

    const inList = requests.some(
      (item: { id: string | number; type?: string }) =>
        String(item.id) === currentId && item.type === currentType,
    );

    if (!inList) {
      return;
    }

    const scrollBehavior = hasPositionedInitialRequestRef.current ? "smooth" : "auto";
    const didScroll = scrollToLogBox(currentId, scrollBehavior);
    if (!didScroll) {
      return;
    }

    hasPositionedInitialRequestRef.current = true;
    lastScrolledRequestIdRef.current = currentId;
    setPositionedRequestId(currentId);
  }, [currentId, currentType, requests, scrollToLogBox]);

  useLayoutEffect(() => {
    const previousScrollPosition = previousScrollPositionRef.current;
    const container = sidebarLogContainerRef.current;

    if (!previousScrollPosition || !container || isFetchingPreviousPage) {
      return;
    }

    if (previousScrollPosition.anchorRequestId && previousScrollPosition.anchorOffsetTop !== null) {
      const anchorElement = container.querySelector(
        `[data-request-id="${previousScrollPosition.anchorRequestId}"]`,
      ) as HTMLElement | null;

      if (anchorElement) {
        const nextAnchorOffsetTop =
          anchorElement.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTop += nextAnchorOffsetTop - previousScrollPosition.anchorOffsetTop;
        previousScrollPositionRef.current = null;
        return;
      }
    }

    container.scrollTop =
      container.scrollHeight -
      previousScrollPosition.scrollHeight +
      previousScrollPosition.scrollTop;
    previousScrollPositionRef.current = null;
  }, [isFetchingPreviousPage, requests.length]);

  const isFetchingNewRequests = requestLogs.isFetching;
  const isFetching = requestLogs.isFetching && !hasNextPage;
  const noRequestsFound = !(requests.length > 0);
  const isInitialLoading = isFetchingNewRequests && noRequestsFound;
  const isRefreshing = isFetchingNewRequests && isFetching && !isInitialLoading;

  return (
    <nav
      className={cn(
        "flex h-full flex-col space-y-1 overflow-x-hidden px-1 pb-4 pt-2",
        isInitialLoading ? "overflow-y-hidden" : "overflow-y-auto",
      )}
      ref={sidebarLogContainerRef}
    >
      {isInitialLoading && <RequestLogsLoadingShimmer />}
      <div
        ref={previousInfiniteScrollRef}
        className={`${hasPreviousPage ? "flex justify-center py-2" : "hidden"}`}
      >
        <LoadingSpinner size={5} />
      </div>
      <RequestLogCards
        requests={requests || []}
        requestId={requestId}
        groupId={groupId}
        recentlyViewedRequestId={recentlyViewedRequestId}
        onViewedRequest={markRecentlyViewedRequest}
      />
      <div
        ref={nextInfiniteScrollRef}
        className={`${hasNextPage ? "flex justify-center py-2" : "hidden"}`}
      >
        <LoadingSpinner size={5} />
      </div>
      {isRefreshing && (
        <div className="flex justify-center py-2">
          <LoadingSpinner size={5} />
        </div>
      )}
      {!isFetching && noRequestsFound && <NoResultsFooter requestsOn={requestsOn} />}
    </nav>
  );
};

export default RequestLogs;
